import { RefObject, useEffect } from 'react';

/**
 * Desliga pointer-events do container ENQUANTO a página rola.
 *
 * Motivo: rolar com a roda do mouse/touchpad mantém o cursor sobre o conteúdo,
 * e a cada frame o navegador refaz hit-testing e recalcula os estilos :hover
 * das linhas que passam sob o ponteiro — na thread principal. Em máquinas mais
 * modestas isso trava a rolagem (teclado e touch não atualizam hover, por isso
 * rolam liso). Sem pointer-events não há hit-testing; reativa ~150ms após o
 * último evento de scroll.
 *
 * IMPORTANTE: aplicar em um FILHO do container que rola, nunca no próprio
 * scroller — com pointer-events none no scroller o hit-test da roda cai no
 * ancestral e a página para de rolar.
 */
export function usePauseHoverOnScroll(ref: RefObject<HTMLElement>): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    let timeout: ReturnType<typeof setTimeout> | null = null;

    const handleScroll = (): void => {
      if (el.style.pointerEvents !== 'none') {
        el.style.pointerEvents = 'none';
      }
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        el.style.pointerEvents = '';
      }, 150);
    };

    // 'scroll' não borbulha, mas com capture pegamos o scroll de qualquer
    // container (o layout privado rola num div interno, não na janela).
    window.addEventListener('scroll', handleScroll, {
      capture: true,
      passive: true,
    });

    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
      if (timeout) clearTimeout(timeout);
    };
  }, [ref]);
}
